import { useCallback } from 'react'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { ICustomerFormSchema, IFormSchemaReport } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { CompleteChecklistsDialog } from 'src/dialogs/CompleteChecklistsDialog'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useMaxCountForCageValidation } from 'src/hooks/useMaxCountForCageValidation'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

interface BaseOptions {
  schemas: ICustomerFormSchema[] | undefined
  isLoadingSchemas?: boolean
  confirmText?: string
  maxCountValidation?: {
    positionId?: EntityId | null
    equipmentIds: EntityId[]
  }
}

interface SingleOptions extends BaseOptions {
  isBulk?: false
  mainComponentId?: EntityId
  onConfirm: (reports: IFormSchemaReport[]) => void | Promise<void>
}

interface BulkOptions extends BaseOptions {
  isBulk: true
  selectedEquipments: IMainComponent[]
  onConfirm: (reportsMap: Map<number, IFormSchemaReport[]>) => void | Promise<void>
}

type ShowChecklistsDialogOptions = SingleOptions | BulkOptions

export const CHECKLISTS_FEATURE_ENABLED = true

export const useCompleteChecklistsDialog = (customerId: EntityId) => {
  const { showDialog } = useDialogCtx()
  const theme = useTheme()
  const { t } = useTranslation('main-component')
  const validateMaxCountForCage = useMaxCountForCageValidation(customerId)

  return useCallback(
    async (options: ShowChecklistsDialogOptions) => {
      const { schemas, isLoadingSchemas, confirmText, maxCountValidation } = options

      if (
        schemas &&
        schemas.length > 0 &&
        maxCountValidation &&
        (await validateMaxCountForCage(maxCountValidation.positionId, maxCountValidation.equipmentIds))
      ) {
        toast.error(t('exceedMaxCountForCage', { ns: 'main-component' }))
        return
      }

      if (!CHECKLISTS_FEATURE_ENABLED || !schemas || schemas.length === 0) {
        if (options.isBulk) {
          await options.onConfirm(new Map())
        } else {
          await options.onConfirm([])
        }
        return
      }

      if (options.isBulk) {
        await showDialog(
          CompleteChecklistsDialog,
          {
            componentProps: {
              customerId,
              schemas,
              isLoadingSchemas,
              isBulk: true,
              selectedEquipments: options.selectedEquipments,
              confirmText,
              returnAsMap: true,
            },
            pop: async (reportsMap: Map<number, IFormSchemaReport[]> | null) => {
              if (reportsMap == null) return
              await options.onConfirm(reportsMap)
            },
            opts: {
              bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
              rootWidth: '1400px',
            },
            sx: { '& .MuiPaper-root': { width: '1200px' } },
          },
          undefined,
          true,
        )
      } else {
        await showDialog(
          CompleteChecklistsDialog,
          {
            componentProps: {
              customerId,
              schemas,
              isLoadingSchemas,
              mainComponentId: options.mainComponentId,
              confirmText,
            },
            pop: async (reports: IFormSchemaReport[] | null) => {
              if (reports == null) return
              await options.onConfirm(reports)
            },
            opts: {
              bgColor: getThemeColor(theme, MUIThemeColors.white),
              rootWidth: '1400px',
            },
            sx: { '& .MuiPaper-root': { width: '1200px' } },
          },
          undefined,
          true,
        )
      }
    },
    [customerId, showDialog, theme, validateMaxCountForCage, t],
  )
}
